import {
  Alert,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { type FC } from 'react';

import { type RouterCapacityTestItem, type RouterCapacityTestResultItem } from '@okvc/schema';

import { profileLabel, sustainableStreamsLabel } from './routerNetworkFormat.js';
import { formatDurationMs } from '../../MediaStatus/format.js';
import SummaryStat from '../../MediaStatus/SummaryStat.js';
import { CARRIER_LABELS } from '../routerSimForm.js';

interface CapacityTestResultsProps {
  test: RouterCapacityTestItem;
}

function fps(result: RouterCapacityTestResultItem): string {
  if (result.receivedFps === null) {
    return '—';
  }
  const expected = result.expectedFps === null ? '' : ` / ${result.expectedFps}`;
  return `${result.receivedFps.toFixed(1)}${expected}`;
}

function optionalDuration(ms: number | null): string {
  return ms === null ? '—' : formatDurationMs(ms);
}

function testHeading(test: RouterCapacityTestItem): string {
  const carrier = test.carrier ? ` on ${CARRIER_LABELS[test.carrier]}` : '';
  return `Tested ${DateTime.fromISO(test.createdAt).toLocaleString(DateTime.DATETIME_MED)}${carrier}`;
}

const CapacityTestResults: FC<CapacityTestResultsProps> = ({ test }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 1 }}>{testHeading(test)}</Typography>
    {test.status === 'failed' ? (
      <Alert severity="error" sx={{ mb: 2 }}>{test.failureReason ?? 'The test did not finish'}</Alert>
    ) : null}
    {test.status === 'cancelled' ? <Alert severity="info" sx={{ mb: 2 }}>The test was cancelled</Alert> : null}
    {test.status === 'completed' ? (
      <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
        <SummaryStat label="Best low-quality profile" value={profileLabel(test.recommendedLowProfile)} emphasised />
        <SummaryStat label="Best high-quality profile" value={profileLabel(test.recommendedHighProfile)} emphasised />
        <SummaryStat label="HQ streams at once" value={sustainableStreamsLabel(test)} emphasised />
        <SummaryStat
          label="LQ streams at once (estimate)"
          value={test.sustainableLowStreams === null ? '—' : `~${test.sustainableLowStreams}`}
        />
      </Stack>
    ) : null}
    {test.results.length > 0 ? (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Step</TableCell>
            <TableCell>Profile</TableCell>
            <TableCell align="right">Streams</TableCell>
            <TableCell align="right">fps received / expected</TableCell>
            <TableCell align="right">Frames delivered</TableCell>
            <TableCell align="right">kbps</TableCell>
            <TableCell align="right">Longest gap</TableCell>
            <TableCell align="right">Startup delay</TableCell>
            <TableCell>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {test.results.map((result) => (
            <TableRow key={result.id}>
              <TableCell>{result.phase === 'ramp' ? 'Load' : `Profile (${result.cameraKind})`}</TableCell>
              <TableCell>{profileLabel(result.profile)}</TableCell>
              <TableCell align="right">{result.concurrentStreams}</TableCell>
              <TableCell align="right">{fps(result)}</TableCell>
              <TableCell align="right">
                {result.deliveryPercent === null ? '—' : `${Math.round(result.deliveryPercent)}%`}
              </TableCell>
              <TableCell align="right">{result.throughputKbps}</TableCell>
              <TableCell align="right">{optionalDuration(result.maxFrameGapMs)}</TableCell>
              <TableCell align="right">{optionalDuration(result.startupMs)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={result.passed ? 'Smooth' : 'Struggled'}
                  color={result.passed ? 'success' : 'error'}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ) : null}
  </Paper>
);

export default CapacityTestResults;
